import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@/methods/devLog'
import { cancelGocardless, getGocardlessContext } from '@/methods/gocardless'
import { cx } from '@/utils/cx'
import { ACCOUNT_INFORMATION_INSUFFICIENT_DATA_REASON } from '@/constants/verifiedTypes'
import { ErrorGocardlessElavonPage } from '@/pages/elavon/ErrorGocardlessElavonPage'

export const GocardlessCheckPage = observer(() => {
  const buttonRef = useRef(null)

  const store = useContext(StoreContext)
  const {
    retryPage: { gocardless: trans },
  } = store.TranslationsState.translations
  const { screenSize, isMobile } = store.AppState

  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [expectedName, setExpectedName] = useState<string | undefined>('')
  const [actualName, setActualName] = useState<string | undefined>('')
  const [failureReason, setFailureReason] = useState<string | undefined>()

  // Mobile utilities
  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  // `isActive` says whether this mount is still the current page: the user may press Forward
  // (or Back again) while the request is in flight, and a late response must then neither
  // navigate nor cancel on their behalf.
  const getContext = async (isActive: () => boolean) => {
    try {
      const data = await getGocardlessContext()
      if (!isActive()) return

      if (!data?.isRetryAttempt) {
        // This step only forwards, so it is where the browser's back button from the screen after
        // this one lands. When the flow lets the user leave open banking, that is what they asked
        // for: cancel instead of pushing the next screen again (which trapped them here).
        if (data?.isCancellable && store.ScenarioState.arrivedByHistoryNavigation) {
          cancelGocardless()
          return
        }
        store.ScenarioState.nextPage()
      } else {
        setExpectedName(data?.expectedData?.accountHolder)
        data?.actualData && setActualName(data?.actualData.accountHolder)
        setFailureReason(data?.actualData?.failureReason)
      }
      setIsLoading(false)
    } catch (error) {
      devLog('Error fetching context:', error)
    }
  }

  const handleProceed = () => {
    setBtnLoading(true)
    store.ScenarioState.nextPage()
  }

  useEffect(() => {
    if (screenSize.width && screenSize.width <= 980) {
      setMobileContainerHeight(`calc(${window.innerHeight}px - 120px)`)
    }
  }, [screenSize.width])

  useEffect(() => {
    let active = true
    void getContext(() => active)
    return () => {
      active = false
    }
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  const tryAgainButton = (
    <div className="layout__content-container-bottom-fixed-centered">
      <Button onClick={handleProceed} tabIndex={0} className="button--proceed" ref={buttonRef}>
        {btnLoading && <LoadingSpinner inButton />}
        <span
          className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
          data-testId="proceed"
        >
          {trans.tryAgain}
        </span>
      </Button>
    </div>
  )

  // The previous attempt returned nothing but the holder name: the names are not what went wrong,
  // so the generic business verification failure page is shown instead of the name comparison.
  if (failureReason === ACCOUNT_INFORMATION_INSUFFICIENT_DATA_REASON) {
    return <ErrorGocardlessElavonPage>{tryAgainButton}</ErrorGocardlessElavonPage>
  }

  return (
    <div className="page-container mobile-m-0">
      <div
        className="layout__content-container-with-scroll"
        style={
          isMobile
            ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
            : undefined
        }
      >
        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph">{trans.description}</p>

        <div className="generic__spacer h-4" />

        <div className="elavon__paragraph-with-icon">
          <p className="generic__paragraph">{trans.businessName}</p>
          {expectedName !== actualName ? (
            <Icon type="mismatch" size="18px" color="#D83535" />
          ) : (
            <Icon type="check-circle" size="18px" color="#467723" />
          )}
        </div>

        <div className="elavon__expected-name-wrapper">{expectedName}</div>
        <div className={cx('elavon__actual-name-wrapper', expectedName !== actualName && 'error')}>
          {actualName}
        </div>

        <div className="generic__spacer h-30" />

        <div className="elavon__warning">
          <div className="elavon__step-icon warning">
            <Icon type="warning" size="18px" color="#303438" />
          </div>
          <div className="elavon__info-text">{trans.tryDifferentAccount}</div>
        </div>

        {isMobile ? (
          <div className="layout__margined m-0-0-40" />
        ) : (
          <div className="layout__margined m-0-0-15" />
        )}
      </div>

      {tryAgainButton}
    </div>
  )
})
